import { _ as _async_to_generator } from "@swc/helpers/_/_async_to_generator";
import { _ as _sliced_to_array } from "@swc/helpers/_/_sliced_to_array";
import { _ as _ts_generator } from "@swc/helpers/_/_ts_generator";
import { jsx as _jsx } from "react/jsx-runtime";
import { Core } from "@faire/design-tokens";
import { ButtonBase, Symbol } from "@faire/retailer-visitor-shared/components/Button/RoundButtonStyles";
import { BackInStockToast } from "@faire/retailer-visitor-shared/features/BackInStock/BackInStockToast";
import { useEventSubscriptionStore } from "@faire/retailer-visitor-shared/stores/domain/EventSubscriptionStore";
import { debounceIfPossible } from "@faire/web--source/common/globals/debounceIfPossible";
import { isTouchDevice } from "@faire/web--source/common/isTouchDevice";
import { useStrictLocalization } from "@faire/web--source/common/localization";
import { NotificationBell } from "@faire/web--source/slate/icons/foundation/NotificationBell";
import { NotificationBellChecked } from "@faire/web--source/slate/icons/foundation/NotificationBellChecked";
import { Column } from "@faire/web--source/slate/Layout";
import { getSpacing } from "@faire/web--source/slate/spacing";
import { createToast } from "@faire/web--source/slate/Toast/Toast";
import { Span } from "@faire/web--source/slate/Typography";
import ClickOutside from "@faire/web--source/ui/ClickOutside";
import React, { useState } from "react";
import styled, { css } from "styled-components";
var TouchDeviceRoundNotifyMeButton = function(param) {
    var handleNotifyMeClick = param.handleNotifyMeClick, componentSize = param.componentSize;
    return /*#__PURE__*/ _jsx(ButtonBackground, {
        $isBase: true,
        $isExpanded: false,
        children: /*#__PURE__*/ _jsx(ButtonBase, {
            "data-test-id": "touch-round-notify-me-base-button",
            $size: componentSize,
            onClick: function(e) {
                e.preventDefault();
                handleNotifyMeClick();
            },
            children: /*#__PURE__*/ _jsx(Symbol, {
                "data-test-id": "base-notify-icon",
                children: /*#__PURE__*/ _jsx(NotificationBell, {})
            })
        })
    });
};
var NonTouchRoundNotifyMeButton = function(param) {
    var handleNotifyMeClick = param.handleNotifyMeClick, setIsExpanded = param.setIsExpanded, componentSize = param.componentSize, isExpanded = param.isExpanded;
    var strictLocalize = useStrictLocalization().strictLocalize;
    if (isExpanded) {
        var notifyMeText = strictLocalize({
            id: "I4xQwC",
            defaultMessage: [
                {
                    type: 0,
                    value: "Notify me"
                }
            ]
        });
        return /*#__PURE__*/ _jsx(ButtonBackground, {
            "data-test-id": "round-notify-me-expanded-button",
            $isBase: false,
            $isExpanded: true,
            onClick: function(e) {
                e.preventDefault();
                handleNotifyMeClick();
            },
            onMouseLeave: function() {
                return setIsExpanded(false);
            },
            children: /*#__PURE__*/ _jsx(Span, {
                variant: "paragraphSansMedium",
                style: {
                    color: "inherit"
                },
                children: notifyMeText
            })
        });
    } else {
        return /*#__PURE__*/ _jsx(ButtonBackground, {
            $isBase: true,
            $isExpanded: false,
            children: /*#__PURE__*/ _jsx(ButtonBase, {
                "data-test-id": "round-notify-me-base-button",
                $size: componentSize,
                onMouseEnter: function() {
                    return setIsExpanded(true);
                },
                children: /*#__PURE__*/ _jsx(Symbol, {
                    "data-test-id": "base-notify-icon",
                    children: /*#__PURE__*/ _jsx(NotificationBell, {})
                })
            })
        });
    }
};
var RoundNotifyMeButtonBase = function(param) {
    var handleNotifyMeClick = param.handleNotifyMeClick, handleNotifiedClick = param.handleNotifiedClick, setIsExpanded = param.setIsExpanded, isClicked = param.isClicked, isSubscriedToBackInStock = param.isSubscriedToBackInStock, componentSize = param.componentSize, isExpanded = param.isExpanded;
    if (isClicked || isSubscriedToBackInStock) {
        return /*#__PURE__*/ _jsx(ButtonBackground, {
            "data-test-id": "round-notified-button",
            $isBase: false,
            $isExpanded: false,
            onClick: function(e) {
                e.preventDefault();
                handleNotifiedClick();
            },
            children: /*#__PURE__*/ _jsx(ButtonBase, {
                $size: componentSize,
                style: {
                    "--size": componentSize
                },
                children: /*#__PURE__*/ _jsx(Symbol, {
                    "data-test-id": "notified-icon",
                    children: /*#__PURE__*/ _jsx(NotificationBellChecked, {
                        color: Core.icon.inverse
                    })
                })
            })
        });
    }
    if (isTouchDevice()) {
        return /*#__PURE__*/ _jsx(TouchDeviceRoundNotifyMeButton, {
            handleNotifyMeClick: handleNotifyMeClick,
            componentSize: componentSize
        });
    } else {
        return /*#__PURE__*/ _jsx(NonTouchRoundNotifyMeButton, {
            handleNotifyMeClick: handleNotifyMeClick,
            setIsExpanded: setIsExpanded,
            componentSize: componentSize,
            isExpanded: isExpanded
        });
    }
};
export var RoundNotifyMeButton = function(param) {
    var productOptionToken = param.productOptionToken, isSubscriedToBackInStock = param.isSubscriedToBackInStock, deleteBackInStockNotification = param.deleteBackInStockNotification;
    var _useState = _sliced_to_array(useState(false), 2), isClicked = _useState[0], setIsClicked = _useState[1];
    var _useState1 = _sliced_to_array(useState(false), 2), isExpanded = _useState1[0], setIsExpanded = _useState1[1];
    var componentSize = getSpacing("4x");
    var debouncedCloseExpandedButton = debounceIfPossible(function() {
        return setIsExpanded(false);
    }, 3000);
    React.useEffect(function() {
        if (isExpanded) {
            debouncedCloseExpandedButton();
        }
    }, [
        isExpanded,
        debouncedCloseExpandedButton
    ]);
    var _useEventSubscriptionStore = useEventSubscriptionStore([
        "subscribeBackInStock",
        "unsubscribeBackInStock"
    ]), subscribeBackInStock = _useEventSubscriptionStore.subscribeBackInStock, unsubscribeBackInStock = _useEventSubscriptionStore.unsubscribeBackInStock;
    var handleNotifyMeClick = /*#__PURE__*/ function() {
        var _ref = _async_to_generator(function() {
            var e;
            return _ts_generator(this, function(_state) {
                switch(_state.label){
                    case 0:
                        _state.trys.push([
                            0,
                            2,
                            3,
                            4
                        ]);
                        return [
                            4,
                            subscribeBackInStock(productOptionToken)
                        ];
                    case 1:
                        _state.sent();
                        createToast(BackInStockToast(), {
                            timeout: "short"
                        });
                        setIsClicked(true);
                        return [
                            3,
                            4
                        ];
                    case 2:
                        e = _state.sent();
                        setIsClicked(false);
                        return [
                            3,
                            4
                        ];
                    case 3:
                        setIsExpanded(false);
                        return [
                            7
                        ];
                    case 4:
                        return [
                            2
                        ];
                }
            });
        });
        return function handleNotifyMeClick() {
            return _ref.apply(this, arguments);
        };
    }();
    var handleNotifiedClick = /*#__PURE__*/ function() {
        var _ref = _async_to_generator(function() {
            var e;
            return _ts_generator(this, function(_state) {
                switch(_state.label){
                    case 0:
                        _state.trys.push([
                            0,
                            3,
                            ,
                            4
                        ]);
                        if (!(deleteBackInStockNotification !== undefined)) return [
                            3,
                            2
                        ];
                        return [
                            4,
                            unsubscribeBackInStock(productOptionToken)
                        ];
                    case 1:
                        _state.sent();
                        deleteBackInStockNotification(productOptionToken);
                        setIsClicked(false);
                        _state.label = 2;
                    case 2:
                        return [
                            3,
                            4
                        ];
                    case 3:
                        e = _state.sent();
                        setIsClicked(true);
                        return [
                            3,
                            4
                        ];
                    case 4:
                        return [
                            2
                        ];
                }
            });
        });
        return function handleNotifiedClick() {
            return _ref.apply(this, arguments);
        };
    }();
    return /*#__PURE__*/ _jsx(ClickOutside, {
        onClickOutside: function() {
            return setIsExpanded(false);
        },
        children: /*#__PURE__*/ _jsx(Column, {
            children: /*#__PURE__*/ _jsx(RoundNotifyMeButtonBase, {
                handleNotifyMeClick: handleNotifyMeClick,
                handleNotifiedClick: handleNotifiedClick,
                setIsExpanded: setIsExpanded,
                isClicked: isClicked,
                isSubscriedToBackInStock: isSubscriedToBackInStock,
                componentSize: componentSize,
                isExpanded: isExpanded
            })
        })
    });
};
var ButtonBackground = styled.div.withConfig({
    componentId: "sc-2ec2da40-0"
})([
    "display:flex;align-items:center;justify-content:center;max-width:max-content;padding-left:",
    ";padding-right:",
    ";height:",
    ";transition:all 150ms linear;border-radius:16px;",
    ";"
], function(props) {
    return props.$isExpanded ? getSpacing("2x") : "0";
}, function(props) {
    return props.$isExpanded ? getSpacing("2x") : "0";
}, getSpacing("4x"), function(param) {
    var $isBase = param.$isBase;
    return $isBase ? css([
        "background-color:",
        ";color:",
        ";"
    ], Core.surface.inverse, Core.text.default) : css([
        "background-color:",
        ";color:",
        ";"
    ], Core.surface.default, Core.text.inverse);
});
